// Types matching the academics backend (MAK-56) Pydantic schemas.
// snake_case mirrors Django responses; no client-side camelCase conversion needed.

// ─── School Years ─────────────────────────────────────────────────────────────

export type SchoolYearStatus = 'PLANNING' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED'

export interface SchoolYear {
  id: string
  school_id: string
  school_name: string
  name: string
  start_date: string
  end_date: string
  status: SchoolYearStatus
  is_current: boolean
  is_registration_open: boolean
  created_by_id: number | null
  created_at: string
  updated_at: string
}

export interface ListSchoolYearsParams {
  school_id?: string
  status?: SchoolYearStatus
}

export interface CreateSchoolYearPayload {
  school_id: string
  name: string
  start_date: string
  end_date: string
  status?: SchoolYearStatus
  is_current?: boolean
  is_registration_open?: boolean
}

/** PATCH `/academics/school-years/:id/` — partial updates */
export interface UpdateSchoolYearPayload {
  name?: string
  start_date?: string
  end_date?: string
  status?: SchoolYearStatus
  is_current?: boolean
  is_registration_open?: boolean
}

/** Non-blocking policy hints returned on successful school-year / window mutations (MAK-228). */
export interface DatePolicyWarning {
  code?: string
  message: string
  field?: string
  meta?: Record<string, unknown>
}

/** Successful mutation envelope may include soft warnings alongside `data`. */
export interface AcademicsMutationResult<T> {
  data: T
  date_policy_warnings?: DatePolicyWarning[]
  message?: string
}

export type SchoolYearCreateResult = AcademicsMutationResult<SchoolYear>

export type SchoolYearUpdateResult = AcademicsMutationResult<SchoolYear>

// ─── School year fees (MAK-57) ────────────────────────────────────────────────
/** Canonical fee defaults for a school year — mirrors registration form default fields when present. */
export interface SchoolYearFeeDefaults {
  id: string
  school_year_id: string
  default_tuition_cadence: string
  default_monthly_tuition_start_month: number | null
  default_monthly_tuition_end_month: number | null
  default_tuition_fee: number
  default_registration_fee: number
  default_has_registration_fee: boolean
  default_activity_fee: number | null
  default_technology_fee: number | null
  default_book_fee: number | null
  default_has_book_fee: boolean
  default_lunch_fee: number | null
  default_has_lunch_fee: boolean
  default_trip_fee: number | null
  default_has_trip_fee: boolean
  default_custom_fee_name: string
  default_custom_fee_amount: number | null
  default_has_custom_fee: boolean
  default_payment_plan_enabled: boolean
  default_installments: number
  allowed_installment_counts: number[] | null
  default_minimum_payment_amount: number | null
  created_at: string
  updated_at: string
}

export type UpdateSchoolYearFeeDefaultsPayload = Partial<
  Omit<SchoolYearFeeDefaults, 'id' | 'school_year_id' | 'created_at' | 'updated_at'>
>

export interface SchoolYearGradeFee {
  id: string
  school_year_id: string
  grade_level_id: number
  grade_level_name?: string
  /** True when this grade follows school-year defaults (`use_year_defaults` on the API). */
  use_form_defaults: boolean
  registration_fee: number
  tuition_fee: number
  activity_fee: number | null
  technology_fee: number | null
  book_fee: number | null
  lunch_fee: number | null
  trip_fee: number | null
}

/** PUT /school-years/:id/fees/grades/:grade_level_id/ — SchoolYearGradeFeeUpdateSchema (partial). */
export type UpdateSchoolYearGradeFeePayload = Partial<{
  use_year_defaults: boolean | null
  registration_fee: number | string | null
  tuition_fee: number | string | null
  activity_fee: number | string | null
  technology_fee: number | string | null
  book_fee: number | string | null
  lunch_fee: number | string | null
  trip_fee: number | string | null
}>

/**
 * GET /school-years/:id/fees/ — snapshot for admin UI and registration create flow.
 * Backend exposes per-grade rows as `grades`; we normalize to `grade_fees` in the client.
 */
export interface SchoolYearFeesSnapshot {
  school_year_id: string
  /** When true, checkout should resolve amounts from these tables instead of legacy form defaults. */
  is_configured: boolean
  defaults: SchoolYearFeeDefaults | null
  grade_fees: SchoolYearGradeFee[]
}

// ─── Academic action windows (MAK-228) ────────────────────────────────────────

export type AcademicActionType =
  | 'GRADE_ENTRY'
  | 'ATTENDANCE_ENTRY'
  | 'SUBJECT_COMMENT'
  | 'REPORT_CARD_GENERATE'
  | 'REPORT_CARD_PUBLISH'
  | 'ROLLOVER_EXECUTE'

export interface AcademicActionWindow {
  id: string
  school_year_id: string
  action_type: AcademicActionType
  opens_on: string
  closes_on: string
  marking_period_id: string | null
  created_at: string
  updated_at: string
}

export interface CreateAcademicActionWindowPayload {
  action_type: AcademicActionType
  opens_on: string
  closes_on: string
  marking_period_id?: string | null
}

export type UpdateAcademicActionWindowPayload = Partial<CreateAcademicActionWindowPayload>

export type AcademicActionWindowMutationResult = AcademicsMutationResult<AcademicActionWindow>

// ─── Academic Class ───────────────────────────────────────────────────────────

export interface AcademicClass {
  id: string
  school_year_id: string
  school_id: string
  name: string
  code: string | null
  grade_level_id: number
  grade_level_name: string
  grade_section_id: number | null
  grade_section_name: string | null
  primary_teacher_id: number | null
  primary_teacher_name: string | null
  exclude_from_gradebook?: boolean
  is_active: boolean
  student_count: number
  /** Active timetable offerings; 0 means the class is not on the timetable. */
  scheduled_offering_count?: number
  created_at: string
  updated_at: string
}

export interface CreateAcademicClassPayload {
  name: string
  grade_level_id: number
  grade_section_id?: number | null
  code?: string | null
  primary_teacher_id?: number | null
  is_active?: boolean
}

export type UpdateAcademicClassPayload = Partial<CreateAcademicClassPayload>

/** MAK-227 — `POST .../classes/bulk/` */
export interface BulkAcademicClassItemInput {
  client_index?: number
  name: string
  grade_level_id: number
  grade_section_id?: number | null
  code?: string | null
  primary_teacher_id?: number | null
  is_active?: boolean
}

/** JSON body item shape expected by backend Pydantic (snake_case; numeric IDs as JSON numbers). */
export interface AcademicClassBulkItemWire {
  name: string
  grade_level_id: number
  grade_section_id: number | null
  code: string
  primary_teacher_id: number | null
  is_active: boolean
  client_index: number
}

export interface AcademicClassBulkRequest {
  items: BulkAcademicClassItemInput[]
}

export interface BulkAcademicClassCreatedItem {
  client_index?: number
  id: string
  school_year_id?: string
  grade_level_id: number
  grade_section_id?: number | null
  name: string
  code?: string | null
  primary_teacher_id?: number | null
  is_active?: boolean
  created_at?: string | null
  updated_at?: string | null
}

export interface BulkClassSkippedItem {
  client_index?: number
  reason: string
  name?: string | null
  grade_level_id?: number | null
  grade_section_id?: number | null
}

export interface BulkRowFailedItem {
  client_index?: number
  error_code?: string
  message?: string
  field_errors?: Record<string, string>
}

export interface AcademicClassBulkResponse {
  created: BulkAcademicClassCreatedItem[]
  skipped: BulkClassSkippedItem[]
  failed: BulkRowFailedItem[]
  dry_run: boolean
}

/** `POST .../classes/bulk-assign-teacher/` — atomic; `teacher_id: null` clears the teacher. */
export interface BulkAssignTeacherPayload {
  class_ids: string[]
  teacher_id: number | null
}

export interface BulkAssignTeacherSkippedItem {
  class_id: string
  reason: 'not_in_school_year' | 'already_assigned'
}

export interface BulkAssignTeacherResult {
  teacher_id: number | null
  updated_ids: string[]
  skipped: BulkAssignTeacherSkippedItem[]
}

/** MAK-227 — `POST .../scheduled-offerings/bulk/` */
export interface BulkScheduledOfferingItemInput {
  client_index?: number
  day_type_period_id: string
  academic_class_id: string
  teacher_id?: number | null
  parallel_track?: number
  room_label?: string
  is_active?: boolean
}

export interface ScheduledClassOfferingBulkRequest {
  items: BulkScheduledOfferingItemInput[]
}

export interface ScheduledClassOfferingBulkResponse {
  created: Array<Record<string, unknown> & { client_index?: number }>
  skipped: Array<{ client_index?: number; reason: string }>
  failed: BulkRowFailedItem[]
  dry_run?: boolean
}

/** `POST .../scheduled-offerings/check-conflicts/`: one proposed placement to check. */
export interface PlacementConflictCheckItem {
  client_index?: number
  day_type_period_id: string
  academic_class_id: string
  teacher_id?: number | null
  /** The placement being moved or edited; it is not compared with itself. */
  ignore_offering_id?: string | null
}

/** An existing placement a proposed one would collide with. */
export interface PlacementClash {
  kind: 'teacher' | 'student'
  offering_id: string
  academic_class_id: string
  academic_class_name: string
  /** Teacher name (empty for students). */
  subject: string
  /** Number of students in both classes (1 for a teacher clash). */
  subject_count: number
}

export interface PlacementConflictCheckResult {
  client_index?: number | null
  clashes: PlacementClash[]
}

export interface ListClassesParams {
  is_active?: boolean
}

// ─── Roster ───────────────────────────────────────────────────────────────────

export type RosterStatus = 'ACTIVE' | 'DROPPED' | 'TRANSFERRED'

export interface RosterEntry {
  id: string
  academic_class_id: string
  student_id: string
  student_school_identifier: string | null
  student_first_name: string
  student_last_name: string
  status: RosterStatus
  enrolled_at: string
  left_at: string | null
  created_at: string
  updated_at: string
}

export interface AddRosterEntryPayload {
  student_id: string
  enrolled_at?: string
}

/** `POST .../classes/:id/roster/bulk/` - listed students and/or the class's whole grade. */
export interface BulkAddRosterPayload {
  student_ids?: string[]
  include_grade_students?: boolean
  enrolled_at?: string
}

export interface BulkAddRosterResult {
  /**
   * `grade_fallback`: the class has a section but no student of the grade has one set,
   * so the whole grade was added.
   */
  scope?: 'grade_fallback' | null
  added: RosterEntry[]
  skipped: Array<{ student_id: string; reason: 'already_on_roster'; status: RosterStatus }>
}

export interface UpdateRosterEntryPayload {
  status?: RosterStatus
  left_at?: string | null
}

// ─── Attendance ───────────────────────────────────────────────────────────────

export type AttendanceStatus = 'PRESENT' | 'ABSENT' | 'TARDY' | 'EXCUSED'

export interface AttendanceEntry {
  student_id: string
  student_name: string
  status: AttendanceStatus
  notes: string | null
}

export interface AttendanceRecord {
  id: string
  academic_class_id: string
  session_date: string
  schedule_slot_key: string | null
  finalized: boolean
  entries: AttendanceEntry[]
  created_at: string
  updated_at: string
}

export interface BulkAttendanceEntryPayload {
  student_id: string
  status: AttendanceStatus
  notes?: string
}

export interface BulkAttendancePayload {
  session_date: string
  entries: BulkAttendanceEntryPayload[]
  finalize?: boolean
  schedule_slot_key?: string
}

export interface ListAttendanceParams {
  start_date?: string
  end_date?: string
}

export interface AttendanceSummaryEntry {
  student_id: string
  student_name: string
  present_count: number
  absent_count: number
  late_count: number
  excused_count: number
  total_sessions: number
  /** 0–1 fraction attended (late and excused count); null when nothing was recorded. */
  attendance_rate: number | null
}

export interface AttendanceSummary {
  academic_class_id: string
  start_date: string
  end_date: string
  entries: AttendanceSummaryEntry[]
}

export interface AttendanceSummaryParams {
  start_date: string
  end_date: string
}

// ─── Rollover ─────────────────────────────────────────────────────────────────

export type RolloverDecision = 'PROMOTE' | 'RETAIN' | 'GRADUATE' | 'WITHDRAW'

/** Backend `RolloverStatusLiteral`. */
export type RolloverStatus = 'PLANNED' | 'EXECUTING' | 'COMPLETED' | 'FAILED'

/** Backend `RolloverResultStatusLiteral`; rows are APPLIED after a successful run. */
export type RolloverOutcomeStatus = 'PENDING' | 'APPLIED' | 'SKIPPED' | 'FAILED'

/** Backend `StudentRolloverOutcomeResponseSchema`. Grade and section names are resolved client-side. */
export interface RolloverOutcome {
  id: string
  student_id: string
  student_school_identifier: string
  student_first_name: string
  student_last_name: string
  /** Returning-students reply used to pre-fill the decision (null: not invited). */
  reenrollment_status?: 'CONFIRMED' | 'DECLINED' | 'PENDING' | null
  needs_attention?: boolean
  attention_reason?: string
  source_grade_level_id: number | null
  /** Present when the student is placed in a section in the source year */
  source_section_id: number | null
  decision: RolloverDecision
  target_grade_level_id: number | null
  /** Mapped target section when section-based progression applies */
  target_section_id: number | null
  result_status: RolloverOutcomeStatus
  result_note: string
  executed_at: string | null
  created_at: string
  updated_at: string
}

/**
 * Counts from `SchoolYearRolloverService.summarize_outcomes`: `decision_<promote|retain|graduate|withdraw>`,
 * `result_<pending|applied|skipped|failed>` and `total_students`. Absent keys mean zero.
 */
export type RolloverSummary = Partial<Record<string, number>>

/** Backend `SchoolYearRolloverRunResponseSchema`. */
export interface Rollover {
  id: string
  school_id: string
  source_school_year_id: string
  target_school_year_id: string
  status: RolloverStatus
  planned_by_id: number | null
  executed_by_id: number | null
  planned_at: string
  execution_started_at: string | null
  completed_at: string | null
  error_message: string
  /** Re-enrollment follow-up job; '' until the rollover executes. */
  reenrollment_status: RolloverReenrollmentStatus
  reenrollment_created_count: number
  reenrollment_note: string
  outcomes: RolloverOutcome[]
  summary: RolloverSummary
}

export type RolloverReenrollmentStatus = '' | 'PENDING' | 'DONE' | 'SKIPPED' | 'FAILED'

export interface RolloverDecisionOverride {
  student_id: string
  decision: RolloverDecision
  target_grade_level_id?: number | null
}

export interface CreateRolloverPlanPayload {
  target_school_year_id: string
  decisions?: RolloverDecisionOverride[]
}

export interface ExecuteRolloverPayload {
  confirm: true
}

// ─── Marking Periods ──────────────────────────────────────────────────────────

export interface MarkingPeriod {
  id: string
  school_year_id: string
  name: string
  start_date: string
  end_date: string
  sequence: number
  is_finalized: boolean
  finalized_at: string | null
  finalized_by_id: number | null
  created_at: string
  updated_at: string
}

/** Response shape for marking period finalize/unfinalize — MAK-135 */
export interface MarkingPeriodFinalizeResult {
  marking_period_id: string
  finalized: boolean
  finalized_at: string | null
  finalized_by_id: number | null
}

export function toMarkingPeriodFinalizeResult(mp: MarkingPeriod): MarkingPeriodFinalizeResult {
  return {
    marking_period_id: mp.id,
    finalized: mp.is_finalized,
    finalized_at: mp.finalized_at,
    finalized_by_id: mp.finalized_by_id,
  }
}

/** GET marking period `.../grade-status/` — MAK-135 (extend when backend schema is fixed) */
export interface MarkingPeriodGradeStatus {
  marking_period_id: string
  is_finalized: boolean
}

export interface CreateMarkingPeriodPayload {
  name: string
  start_date: string
  end_date: string
  sequence: number
}

export type UpdateMarkingPeriodPayload = Partial<CreateMarkingPeriodPayload>

export type MarkingPeriodMutationResult = AcademicsMutationResult<MarkingPeriod>

// ─── School-Year Overview ─────────────────────────────────────────────────────

export interface OverviewSchool {
  id: string
  name: string
  code: string
  is_active: boolean
  current_school_year_id: string | null
  matches_school_current_pointer: boolean
  is_active_operating_year: boolean
  note: string
}

export interface OverviewRegistrationMetrics {
  [key: string]: unknown
}

export interface OverviewRegistrationForm {
  id: string
  form_name: string
  is_published: boolean
  registration_status: string
  open_date: string
  close_date: string
}

export interface OverviewRegistration {
  forms: OverviewRegistrationForm[]
  forms_count: number
  metrics: OverviewRegistrationMetrics
}

export interface OverviewGradeSection {
  id: number
  name: string
}

export interface OverviewGradeLevel {
  id: number
  name: string
  sections: OverviewGradeSection[]
}

export interface OverviewSchoolStructure {
  grade_level_count: number
  section_count: number
  grade_levels: OverviewGradeLevel[]
}

export interface OverviewCalendarEvent {
  id: number | string
  title: string
  start_date: string
  end_date: string
  event_type: string
}

export interface OverviewCalendar {
  events_in_range_count: number
  events_preview: OverviewCalendarEvent[]
}

export interface OverviewPaymentRow {
  status: string
  count: number
  total: number | string
}

export interface OverviewYearScopedPayments {
  total_collected_paid: number | string
  total_outstanding: number | string
  payment_rows_by_status: OverviewPaymentRow[]
}

export interface OverviewPayments {
  year_scoped: OverviewYearScopedPayments
  prior_year_open_balance_hint: number | string | null
  prior_year_note: string | null
}

export interface SchoolYearOverviewSchoolYear {
  id: string
  school_id: string
  school_name: string
  name: string
  start_date: string
  end_date: string
  status: string
  is_current: boolean
  is_registration_open: boolean
}

export interface SchoolYearOverview {
  school_year: SchoolYearOverviewSchoolYear
  marking_periods: MarkingPeriod[]
  school: OverviewSchool
  registration: OverviewRegistration
  school_structure: OverviewSchoolStructure
  calendar: OverviewCalendar
  payments: OverviewPayments
}

// ─── Gradebook (MAK-106 / backend MAK-61; summary count semantics MAK-126) ─────

/** Grade row status — MAK-131 / MAK-135 */
export type GradeStatus = 'GRADED' | 'INCOMPLETE' | 'EXCUSED' | 'MISSING'

export interface GradebookAssignment {
  id: string
  academic_class_id: string
  marking_period_id: string
  title: string
  description: string | null
  max_points: number
  due_date: string | null
  is_active: boolean
  category_id: string | null
  created_by_id: number | null
  created_at: string
  updated_at: string
}

export interface CreateGradebookAssignmentPayload {
  title: string
  description?: string | null
  max_points: number
  due_date?: string | null
  is_active?: boolean
  category_id?: string | null
}

export type UpdateGradebookAssignmentPayload = Partial<
  Pick<
    CreateGradebookAssignmentPayload,
    'title' | 'description' | 'max_points' | 'due_date' | 'is_active' | 'category_id'
  >
>

// ─── Teacher subject comments — MAK-130 / MAK-134 ──────────────────────────────

export interface SubjectComment {
  id: string
  academic_class_id: string
  student_id: string
  marking_period_id: string
  author_id: number
  author_name: string
  body: string
  created_at: string
  updated_at: string
}

export interface UpsertSubjectCommentPayload {
  body: string
}

// ─── Assignment categories (weighted averages) — MAK-133 / MAK-129 ─────────────

export interface AssignmentCategory {
  id: string
  academic_class_id: string
  marking_period_id: string
  name: string
  /** 0–100 */
  weight: number
  created_at: string
  updated_at: string
}

export interface CreateAssignmentCategoryPayload {
  name: string
  weight: number
}

export type UpdateAssignmentCategoryPayload = Partial<CreateAssignmentCategoryPayload>

export interface StudentGradeEntry {
  id: string
  assignment_id: string
  academic_class_id: string
  marking_period_id: string
  student_id: string
  student_school_identifier: string | null
  student_first_name: string
  student_last_name: string
  score: number | null
  status: GradeStatus
  notes: string | null
  recorded_by_id: number | null
  is_finalized: boolean
  finalized_at: string | null
  created_at: string
  updated_at: string
}

export interface BulkGradeEntryPayload {
  student_id: string
  /** Required when GRADED; send null for other statuses and omit when clearing. */
  score?: number | null
  /** Remove the saved entry, returning the student to "not yet graded". */
  clear?: boolean
  /** Empty string or omit; do not send JSON null — backend is `str`, not optional null. */
  notes?: string
  /** Defaults to GRADED when omitted — MAK-131 / MAK-135 */
  status?: GradeStatus
}

/** Per-assignment, per-student grade history — MAK-131 / MAK-135 */
export interface GradeChangeLog {
  id: string
  assignment_id: string
  student_id: string
  changed_by_id: number
  changed_by_name: string
  old_score: number | null
  new_score: number | null
  old_status: GradeStatus | null
  /** null when the entry was cleared */
  new_status: GradeStatus | null
  changed_at: string
  note: string | null
}

/** Class-wide grade change log row — MAK-135 */
export interface GradeChangeLogEntry {
  id: string
  student_id: string
  assignment_id: string
  old_score: number | null
  new_score: number | null
  changed_by_id: number
  changed_at: string
  reason: string
}

export interface BulkSaveGradesPayload {
  entries: BulkGradeEntryPayload[]
  finalize?: boolean
}

/**
 * Per-assignment row in `GET .../gradebook/summary/`. Counts `grade_entry_count` and
 * `finalized_entry_count` are scoped to the same **active-roster** population as the rest
 * of the summary (dropped/transferred students excluded) — MAK-126.
 */
export interface GradebookSummaryAssignmentRow {
  assignment_id: string
  title: string
  max_points: number
  due_date: string | null
  grade_entry_count: number
  finalized_entry_count: number
  average_score: number | null
  average_percent: number | null
}

export interface GradebookSummaryStudentRow {
  student_id: string
  student_school_identifier: string | null
  student_first_name: string
  student_last_name: string
  graded_assignment_count: number
  total_scored_points: number
  total_possible_points: number
  percent_grade: number | null
  /** Letter grade from the class grade scale (MAK-128 / MAK-132) */
  letter_grade: string | null
  /** GPA points from the class grade scale (MAK-128 / MAK-132) */
  gpa_points: number | null
}

export interface GradebookSummary {
  academic_class_id: string
  marking_period_id: string
  assignment_count: number
  student_count: number
  graded_entry_count: number
  class_average_percent: number | null
  /** How class average is computed — MAK-129 */
  average_method: 'weighted' | 'simple'
  assignments: GradebookSummaryAssignmentRow[]
  students: GradebookSummaryStudentRow[]
}

// ─── Grade scales & letter grades (MAK-128 / MAK-132) ─────────────────────────

export interface GradeScaleRange {
  id: string
  grade_scale_id: string
  min_score: number
  max_score: number
  letter_grade: string
  gpa_points: number
  description: string | null
}

export interface GradeScale {
  id: string
  school_id: string
  name: string
  is_default: boolean
  ranges: GradeScaleRange[]
  created_at: string
  updated_at: string
}

export interface CreateGradeScalePayload {
  name: string
  is_default?: boolean
}

export interface UpdateGradeScalePayload {
  name?: string
  is_default?: boolean
}

export type ReplaceGradeScaleRangesPayload = Omit<GradeScaleRange, 'id' | 'grade_scale_id'>[]

// ─── Class schedule / timetable (MAK-124) ─────────────────────────────────────

/** Query filters for `GET .../school-years/{id}/timetable/` */
export interface TimetableListParams {
  academic_class_id?: string
  teacher_id?: number
  weekday?: number
  date_from?: string
  date_to?: string
  is_active?: boolean
  /** MAK-218 — filter by linked day-type period */
  day_type_period_id?: string
}

export interface ClassScheduleSlot {
  id: string
  school_year_id: string
  academic_class_id: string
  academic_class_name: string
  /** Parallel track within the same period (MAK-218) */
  parallel_track?: number
  weekday: number
  start_time: string
  end_time: string
  effective_start_date: string
  effective_end_date: string
  teacher_override_id: number | null
  resolved_teacher_id: number | null
  resolved_teacher_name?: string | null
  /** MAK-218 — explicit teacher list (may coexist with override) */
  teacher_ids?: number[]
  room_label: string
  /** Stable key; use for attendance `schedule_slot_key` on period attendance flows */
  slot_key: string
  is_active: boolean
  created_at: string
  updated_at: string
  /** MAK-218 — link to school day-type period (optional) */
  day_type_period_id?: string | null
  day_type_id?: string | null
  day_type_name?: string | null
  day_type_period_name?: string | null
  /** Enrollment / roster targets for this slot (optional) */
  target_grade_level_ids?: number[]
  target_grade_section_ids?: number[]
  target_student_ids?: string[]
}

/**
 * POST `/school-years/{id}/school-schedule/scheduled-offerings/` — places a class in a day-type
 * period (MAK-222). Older free-form slot fields are no longer accepted by the API.
 */
export interface CreateScheduledOfferingPayload {
  day_type_period_id: string
  academic_class_id: string
  teacher_id?: number | null
  parallel_track?: number
  room_label?: string
  is_active?: boolean
}

/** PATCH `.../scheduled-offerings/{offering_id}/` */
export interface UpdateScheduledOfferingPayload {
  teacher_id?: number | null
  parallel_track?: number | null
  room_label?: string | null
  is_active?: boolean | null
  unset_teacher?: boolean
}

// ─── School-first schedule (MAK-164) ─────────────────────────────────────────

export interface SchoolScheduleConfig {
  operating_weekdays: number[]
  school_day_start: string | null
  school_day_end: string | null
  /** Date (YYYY-MM-DD) whose ISO week starts rotation week 0; unset means the year start. */
  rotation_anchor_date?: string | null
  /** Rotation length in weeks (1-52). */
  rotation_cycle_weeks?: number
}

export interface BellPeriod {
  id: number
  school_year_id: string
  name: string
  start_time: string
  end_time: string
  sequence: number
  is_active: boolean
}

export type ScheduleTemplateScope = 'SCHOOL_DEFAULT' | 'GRADE_LEVEL' | 'GRADE_SECTION'

export interface ScheduleTemplateEntry {
  id: number
  template_id: number
  weekday: number
  bell_period_id: number | null
  bell_period_name: string | null
  start_time: string
  end_time: string
}

export interface ScheduleTemplate {
  id: number
  school_year_id: string
  scope: ScheduleTemplateScope
  grade_level_id: number | null
  grade_level_name: string | null
  grade_section_id: number | null
  grade_section_name: string | null
  entries: ScheduleTemplateEntry[]
}

/** One dated session of a class from `GET .../timetable/` (used for "upcoming sessions"). */
export interface ClassSession {
  session_date: string
  weekday: number
  day_type_name: string
  period_name: string
  start_time: string
  end_time: string
  resolved_teacher_name: string | null
  room_label: string
  offering_id: string
}

/** One weekly placement of a class in a bell-schedule period (no dates). */
export interface ScheduledOffering {
  id: string
  day_type_period_id: string
  academic_class_id: string
  academic_class_name: string
  teacher_id: number | null
  resolved_teacher_id: number | null
  /** Display name of the resolved teacher (additive field; null when unassigned). */
  resolved_teacher_name?: string | null
  instructors?: Array<{
    user_id: number
    role: OfferingInstructorRole
    sort_order: number
    name?: string | null
  }>
  parallel_track: number
  room_label: string
  offering_key?: string
  is_active: boolean
}

/** Recurring conflict pattern from `GET .../school-schedule/conflicts/?grouped=true`. */
export interface ScheduleConflictGroup {
  kind: 'teacher' | 'room' | 'student'
  day_type_period_id: string
  period_name: string
  day_type_name: string
  start_time: string | null
  end_time: string | null
  /** 0 = Monday … 6 = Sunday */
  weekdays: number[]
  occurrences: number
  first_date: string
  last_date: string
  /** Teacher name, room label, or student name(s) */
  subject: string
  subject_count: number
  classes: Array<{ offering_id: string; academic_class_id: string; academic_class_name: string }>
  offering_ids: string[]
  /** Affected students (by name) for student conflicts; empty for teacher/room conflicts. */
  students?: Array<{ student_id: string; name: string }>
}

export interface SchoolSchedule {
  school_year_id: string
  /** One row per weekly placement; present once a schedule is configured. */
  scheduled_offerings?: ScheduledOffering[]
  config: SchoolScheduleConfig
  /** Removed in MAK-222 — omitted when absent */
  bell_periods?: BellPeriod[]
  templates?: ScheduleTemplate[]
  configured: boolean
  /**
   * MAK-218 — when present on `GET .../school-schedule/`, day types are included in the aggregate
   * (avoids extra round-trips). Empty array means API is v2 but none created yet.
   */
  day_types?: DayType[]
  weekday_day_type_map?: Record<string, string | null>
  /** MAK-222 — rotation rows; frontend may derive `weekday_day_type_map` for cycle week 0 */
  rotation_weekday_assignments?: { entries?: RotationWeekdayAssignmentInput[] }
  /** Normalized rotation rows (parsed in the client from `rotation_weekday_assignments`) */
  rotation_entries?: RotationWeekdayAssignmentInput[]
}

export interface PatchSchoolScheduleConfigPayload {
  operating_weekdays?: number[]
  /** Sent to the API as `school_day_start_time` (null clears it). */
  school_day_start?: string | null
  /** Sent to the API as `school_day_end_time` (null clears it). */
  school_day_end?: string | null
  rotation_anchor_date?: string | null
  rotation_cycle_weeks?: number
}

export interface CreateBellPeriodPayload {
  name: string
  start_time: string
  end_time: string
  sequence?: number
  is_active?: boolean
}

export type UpdateBellPeriodPayload = Partial<CreateBellPeriodPayload>

export interface CreateScheduleTemplatePayload {
  scope: ScheduleTemplateScope
  grade_level_id?: number | null
  grade_section_id?: number | null
}

export interface ScheduleTemplateEntryInput {
  weekday: number
  bell_period_id?: number | null
  start_time: string
  end_time: string
}

export interface PutScheduleTemplateEntriesPayload {
  entries: ScheduleTemplateEntryInput[]
}

export interface SchoolSchedulePreviewParams {
  date_from: string
  date_to: string
  grade_level_id?: number
  grade_section_id?: number
}

export interface ResolvedSessionsParams {
  academic_class_id: string
  date_from: string
  date_to: string
}

export interface PreviewSession {
  date: string
  weekday: number
  bell_period_id: number | null
  bell_period_name: string | null
  start_time: string
  end_time: string
}

export interface SchedulePreviewResult {
  sessions: PreviewSession[]
}

export interface ResolvedSession {
  date: string
  weekday: number
  start_time: string
  end_time: string
  bell_period_id: number | null
  bell_period_name: string | null
  academic_class_id: string
}

export interface ResolvedSessionsResult {
  sessions: ResolvedSession[]
}

// ─── Day types + weekday mapping (MAK-218) — replaces weekly templates in UI ──

export interface DayTypePeriod {
  id: string
  day_type_id: string
  name: string
  start_time: string
  end_time: string
  sequence: number
  is_active: boolean
}

export interface DayType {
  id: string
  school_year_id: string
  name: string
  sort_order?: number
  is_default?: boolean
  periods?: DayTypePeriod[]
}

export interface WeekdayDayTypeMap {
  weekday_to_day_type_id: Record<string, string | null>
}

export interface CreateDayTypePayload {
  name: string
  sort_order?: number
  is_default?: boolean
}

export type UpdateDayTypePayload = Partial<CreateDayTypePayload>

export interface CreateDayTypePeriodPayload {
  name: string
  start_time: string
  end_time: string
  sequence?: number
  is_active?: boolean
}

export type UpdateDayTypePeriodPayload = Partial<CreateDayTypePeriodPayload>

/** One period of a PUT `.../day-types/{id}/periods/` replacement (sequence = list position). */
export interface ReplaceDayTypePeriodItem {
  name: string
  start_time: string
  end_time: string
}

export interface ReplaceDayTypePeriodsPayload {
  periods: ReplaceDayTypePeriodItem[]
  /** Validate and report what would be removed without saving anything. */
  dry_run?: boolean
}

export interface ReplaceDayTypePeriodsResult {
  dry_run: boolean
  removed_period_count: number
  /** Classes placed in the old periods; they come off the timetable. */
  removed_placement_count: number
  removed_class_count: number
  /** Empty on a dry run. */
  periods: DayTypePeriod[]
}

/** PUT `/school-schedule/rotation-weekday-assignments/` — replaces all rows (MAK-222) */
export interface RotationWeekdayAssignmentInput {
  cycle_week_index: number
  /** 0 = Monday … 6 = Sunday (matches `date.weekday()` in the API) */
  weekday: number
  day_type_id: string
}

export interface PutRotationWeekdayAssignmentsPayload {
  entries: RotationWeekdayAssignmentInput[]
}

/** PUT `.../scheduled-offerings/{id}/instructors/` */
export type OfferingInstructorRole = 'PRIMARY' | 'CO_TEACHER'

export interface OfferingInstructorEntry {
  user_id: number
  role: OfferingInstructorRole
  sort_order?: number
}

export interface OfferingInstructorsReplacePayload {
  entries: OfferingInstructorEntry[]
}

/** PUT `.../scheduled-offerings/{id}/student-overrides/` */
export type OfferingStudentOverrideKind = 'OPT_OUT' | 'ADD_ON'

export interface OfferingStudentOverrideEntry {
  student_id: string
  kind: OfferingStudentOverrideKind
}

/** GET `.../scheduled-offerings/{id}/student-overrides/` row (what PUT replaces) */
export interface OfferingStudentOverrideRow extends OfferingStudentOverrideEntry {
  student_name: string
}

export interface OfferingStudentOverridesReplacePayload {
  entries: OfferingStudentOverrideEntry[]
}

export interface PeriodOfferingsParams {
  session_date: string
  day_type_period_id: string
}

/** GET `.../period-offerings/` — classes in one period on a date */
export interface PeriodOfferingRow {
  id?: string
  academic_class_id?: string
  academic_class_name?: string
  parallel_track?: number
  room_label?: string
  [key: string]: unknown
}

export interface ScheduleConflictsParams {
  date_from: string
  date_to: string
  /** Collapse per-date rows into recurring patterns (smaller payload, names included). */
  grouped?: boolean
}

export type PutWeekdayDayTypeMapPayload = WeekdayDayTypeMap

// ─── Bulk report card generation — background job (202 + polling) ─────────────

export interface ReportCardBulkGeneratePayload {
  marking_period_id: string
  grade_level_ids?: number[]
  class_ids?: string[]
  /** Restrict generation to these students (cohort filters still apply). */
  student_ids?: string[]
}

export interface ReportCardBulkGenerateResult {
  generated: number
  regenerated: number
  skipped: number
  errors: Array<{ student_id: string; student_name: string; error: string }>
}

export type ReportCardBulkJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED'

/** Status of a background bulk report-card job; result counts fill in as it runs. */
export interface ReportCardBulkJob extends ReportCardBulkGenerateResult {
  id: string
  school_year_id: string
  marking_period_id: string
  status: ReportCardBulkJobStatus
  total_students: number
  processed_students: number
  error_message: string
  created_at: string
  completed_at: string | null
}

// ─── Teacher dashboard `/academics/me/*` — MAK-196 / dashboards-design §7.1 ───

/** Query params for `teacherMeApi` (`/academics/me/*`). Backend may require `school_year_id` on some routes. */
export interface TeacherMeQueryParams {
  school_id?: string
  school_year_id?: string
  day?: string
  days?: number
  /** `/me/assignments/upcoming/`: also include assignments due up to this many days ago. */
  past_days?: number
  is_active?: boolean | null
}

/** One dated class session from `GET /academics/me/timetable/` (the teacher's own day). */
export interface TeacherTimetableSession {
  session_date: string
  weekday: number
  period_name: string
  start_time: string
  end_time: string
  academic_class_id: string
  academic_class_name: string
  room_label: string
  offering_id: string
}

/** Shared `{ school_year_id }` scope for teacher `/me/*` routes that require a year. */
export type TeacherMeSchoolYearScope = { school_year_id: string }

/** Lower-case codes exactly as the API sends them. */
export type TeacherAttentionReason = 'absence_streak' | 'low_grade' | 'missing_work'

export interface TeacherAttentionListItem {
  student_id: string
  full_name: string
  class_id: string
  class_name: string
  reason: TeacherAttentionReason
}

export interface TeacherUpcomingAssignmentGradingProgress {
  graded: number
  total: number
}

export interface TeacherUpcomingAssignment {
  assignment_id: string
  title: string
  class_id: string
  class_name: string
  due_date: string
  grading_progress: TeacherUpcomingAssignmentGradingProgress
}

export interface TeacherWeeklyClassAttendance {
  class_id: string
  class_name: string
  /** Week-to-date percentage in [0, 100], or null when no sessions were recorded. */
  attendance_rate: number | null
}

/** Optional envelope if the API wraps the weekly list with a KPI helper */
export interface TeacherWeeklyAttendancePayload {
  classes?: TeacherWeeklyClassAttendance[]
  /** Mean attendance rate for today across classes (0–100), when provided */
  today_mean_attendance_rate?: number | null
}

// ─── Report cards & transcripts (MAK-104) ───────────────────────────────────
export type {
  ListReportCardsParams,
  ListTranscriptsParams,
  PublishedDocumentStatus,
  ReportCardCorrectPayload,
  ReportCardDetail,
  ReportCardGeneratePayload,
  ReportCardListItem,
  ReportCardSnapshot,
  ReportCardSnapshotClass,
  ReportCardStatus,
  SnapshotJsonValue,
  TranscriptDetail,
  TranscriptGeneratePayload,
  TranscriptListItem,
  TranscriptSnapshot,
} from './reportCardsTranscripts'
